import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { buildQueryString, parseQueryString, type FilterClause, type ListQuery, type WhereGroup } from '../api/client';
import type { SortState } from '../views/DataGrid';
import type { ViewKind } from '../meta/types';

// List screen state lives in the URL (shareable, survives refresh/back): the DSL params
// (filter/sort/q) plus `view`, `group`, and the `new`/`edit` drawer flags.

export interface ListState {
  query: ListQuery;
  filters: FilterClause[];
  /** Advanced conditions (nested AND/OR), or null. */
  where: WhereGroup | null;
  sort: SortState[];
  q: string;
  view: ViewKind;
  groupBy: string | null;
  savedView: string | null;
  /** The connected system's view applied underneath the filters (null on NovaPSA or none chosen). */
  sourceView: string | null;
  setFilters: (f: FilterClause[]) => void;
  setWhere: (w: WhereGroup | null) => void;
  addFilter: (c: FilterClause) => void;
  setSort: (s: SortState[]) => void;
  setQ: (q: string) => void;
  setView: (v: ViewKind) => void;
  setGroupBy: (g: string | null) => void;
  setSavedView: (id: string | null) => void;
  /** Replace filters+sort wholesale (saved views). */
  apply: (patch: { filters?: FilterClause[]; where?: WhereGroup | null; sort?: SortState[]; q?: string; view?: ViewKind; groupBy?: string | null; sourceView?: string | null; savedView?: string | null }) => void;
  reset: () => void;
  /** Drawer flags: ?new=1 / ?edit=<id> */
  newOpen: boolean;
  editId: string | null;
  openNew: () => void;
  openEdit: (id: string) => void;
  closeForm: () => void;
}

function sortToParam(s: SortState[]): string[] {
  return s.map((x) => (x.desc ? `-${x.id}` : x.id));
}
function paramToSort(p: string | string[] | undefined): SortState[] {
  const arr = Array.isArray(p) ? p : p ? p.split(',') : [];
  return arr.filter(Boolean).map((k) => (k.startsWith('-') ? { id: k.slice(1), desc: true } : { id: k, desc: false }));
}

const NON_DSL = new Set(['view', 'group', 'new', 'edit', 'sv', 'tab', 'log', 'open']);
const EMPTY_FILTERS: FilterClause[] = [];

/** URL-backed params, or a local store for embedded lists (several on one page). */
function useParamsStore(local: boolean): [URLSearchParams, (next: URLSearchParams, opts?: { replace?: boolean }) => void] {
  const [localParams, setLocalParams] = useState(() => new URLSearchParams());
  const [urlParams, setUrlParams] = useSearchParams();
  if (local) return [localParams, (next) => setLocalParams(new URLSearchParams(next))];
  return [urlParams, setUrlParams];
}

export function useListState(defaults: { sort?: string; view?: ViewKind; filters?: FilterClause[] } = {}, opts: { local?: boolean } = {}): ListState {
  const [params, setParams] = useParamsStore(Boolean(opts.local));

  const dslString = useMemo(() => {
    const p = new URLSearchParams();
    params.forEach((v, k) => {
      if (!NON_DSL.has(k)) p.append(k, v);
    });
    return p.toString();
  }, [params]);
  const parsed = useMemo(() => parseQueryString(dslString), [dslString]);

  const filters = useMemo(() => parsed.filter ?? defaults.filters ?? EMPTY_FILTERS, [parsed.filter, defaults.filters]);
  const where = parsed.where ?? null;
  const sort = useMemo(() => (parsed.sort ? paramToSort(parsed.sort) : paramToSort(defaults.sort)), [parsed.sort, defaults.sort]);
  const q = parsed.q ?? '';
  const view = (params.get('view') as ViewKind | null) ?? defaults.view ?? 'table';
  const groupBy = params.get('group');
  const savedView = params.get('sv');
  const sourceView = parsed.sourceView ?? null;

  const write = useCallback(
    (next: { filters?: FilterClause[]; where?: WhereGroup | null; sort?: SortState[]; q?: string; view?: ViewKind; groupBy?: string | null; savedView?: string | null; sourceView?: string | null; newOpen?: boolean; editId?: string | null }) => {
      const cur = new URLSearchParams(params);
      const f = next.filters ?? filters;
      const s = next.sort ?? sort;
      const qq = next.q ?? q;
      const w = next.where === undefined ? where : next.where;
      const sv0 = next.sourceView === undefined ? sourceView : next.sourceView;
      const dsl = new URLSearchParams(buildQueryString({ filter: f, where: w, sort: sortToParam(s), q: qq, sourceView: sv0 }).slice(1));
      const out = new URLSearchParams();
      dsl.forEach((v, k) => out.append(k, v));
      const v = next.view ?? view;
      if (v && v !== (defaults.view ?? 'table')) out.set('view', v);
      const g = next.groupBy === undefined ? groupBy : next.groupBy;
      if (g) out.set('group', g);
      const sv = next.savedView === undefined ? savedView : next.savedView;
      if (sv) out.set('sv', sv);
      const nw = next.newOpen === undefined ? cur.has('new') : next.newOpen;
      if (nw) out.set('new', '1');
      const ed = next.editId === undefined ? cur.get('edit') : next.editId;
      if (ed) out.set('edit', ed);
      for (const k of ['tab', 'log', 'open']) {
        const existing = cur.get(k);
        if (existing) out.set(k, existing);
      }
      setParams(out, { replace: true });
    },
    [params, filters, where, sort, q, view, groupBy, savedView, sourceView, defaults.view, setParams],
  );

  const query = useMemo<ListQuery>(() => ({ filter: filters.length ? filters : undefined, where: where ?? undefined, sort: sort.length ? sortToParam(sort) : undefined, q: q || undefined, sourceView: sourceView ?? undefined }), [filters, where, sort, q, sourceView]);

  return {
    query,
    filters,
    where,
    sort,
    q,
    view,
    groupBy,
    savedView,
    sourceView,
    setFilters: (f) => write({ filters: f, savedView: null }),
    setWhere: (w) => write({ where: w, savedView: null }),
    addFilter: (c) => write({ filters: [...filters.filter((x) => !(x.field === c.field && x.op === c.op)), c], savedView: null }),
    setSort: (s) => write({ sort: s, savedView: null }),
    setQ: (qq) => write({ q: qq }),
    setView: (v) => write({ view: v }),
    setGroupBy: (g) => write({ groupBy: g }),
    setSavedView: (id) => write({ savedView: id }),
    apply: (patch) => write({ savedView: undefined, ...patch }),
    reset: () => write({ filters: [], where: null, sort: paramToSort(defaults.sort), q: '', groupBy: null, savedView: null, sourceView: null }),
    newOpen: params.has('new'),
    editId: params.get('edit'),
    openNew: () => write({ newOpen: true }),
    openEdit: (id) => write({ editId: id }),
    closeForm: () => write({ newOpen: false, editId: null }),
  };
}
